import { ChartNoAxesCombined, Wallet, ListTodo, UserCog, ListCheck, Search, ArrowLeft, Mail, TrendingUp, CalendarClock, PackageSearch, LayoutList } from "lucide-react"
import mp2Icon from '@/assets/lsp_logos/mp2_logo.png'
import { useEffect, type ComponentType } from "react"

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { Link, useLocation } from "@tanstack/react-router"
import { useStore } from "@/store"
import { useShallow } from "zustand/shallow"
import { SHIKHAR_HOME, getLspReportBppId, is_shikhar_account, is_bigbasket_account, is_rebelfoods_account } from "@/lib/utils"

type NavItem = {
  title: string
  icon: ComponentType<{ color?: string }>
  link: string
  search?: Record<string, unknown>
}

// Menu items.
const baseItems: NavItem[] = [
  {
    title: "Orders",
    icon: ListCheck,
    link: '/orders'
  },
  {
    title: "Wallet",
    icon: Wallet,
    link: '/wallet'
  },
  {
    title: "Reports",
    icon: ChartNoAxesCombined,
    link: '/reports'
  },
  {
    title: "Issues",
    icon: ListTodo,
    link: '/issues'
  },
  {
    title: "Manage",
    icon: UserCog,
    link: '/manage'
  },
  {
    title: "Search Order",
    icon: Search,
    link: '/orders/<orderId>',
    search: { search: true }
  },
]

export function AppSidebar({ curPage }: { curPage: string }) {
  const { setOpenMobile } = useSidebar()
  const { accountId, clearAuth } = useStore(useShallow(state => ({
    accountId: state.selectedAccount.id,
    clearAuth: state.clearAuth
  })))
  const location = useLocation()
  const isShikhar = is_shikhar_account(accountId)
  const hasLspReport = !!getLspReportBppId(accountId)
  const navItems = baseItems.map(item => (
    item.title === 'Reports' && hasLspReport ? { ...item, link: '/reports/lsp' } : item
  ))
  // Gig-specific pages - same gate as Slots, since gigs only exist for these accounts.
  const isSlotsEnabled = accountId === 'mp2_preprod' || is_bigbasket_account(accountId) || is_rebelfoods_account(accountId)
  const visibleItems: NavItem[] = [
    ...(isShikhar ? navItems.filter(item => item.title !== "Manage" && item.title !== "Reports") : navItems),
    { title: "Surge", icon: TrendingUp, link: '/surge' },
    ...(isSlotsEnabled ? [{ title: "Gigs", icon: LayoutList, link: '/gigs' }] : []),
    ...(isSlotsEnabled ? [{ title: "Search Gig", icon: PackageSearch, link: '/gigs/<gigId>', search: { search: true } }] : []),
    ...(isSlotsEnabled ? [{ title: "Slots", icon: CalendarClock, link: '/slots' }] : []),
    { title: "Support", icon: Mail, link: '/support' }
  ]

  useEffect(() => {
    // Close the hamburger when the route changes (selection made)
    setOpenMobile(false)
  }, [location.pathname, setOpenMobile])

  return (
    <Sidebar>
      <SidebarContent>
        <SidebarHeader>
          <div className="flex items-center justify-center w-full py-4">
            <img src={mp2Icon} className="w-14" alt="MP2 Logo" />
          </div>
        </SidebarHeader>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild size='lg'>
                    <Link
                      to={item.link}
                      search={item.search}
                      onClick={() => setOpenMobile(false)}
                      className="cursor-pointer"
                    >
                      <item.icon color={curPage === item.title ? '#155dfc' : 'black'} />
                      <span className={`text-[16px] ${curPage === item.title?'text-blue-500 font-semibold':'text-black'}`}>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {isShikhar ? (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild size='lg'>
                    <a
                      href={SHIKHAR_HOME}
                      onClick={(e) => {
                        e.preventDefault()
                        clearAuth()
                        setOpenMobile(false)
                        window.location.href = SHIKHAR_HOME
                      }}
                      className="cursor-pointer"
                    >
                      <span className="text-[16px] text-black flex items-center gap-2">
                        <ArrowLeft className="h-4 w-4" />
                        Back to Shikhar
                      </span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : null}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  )
}
